import React, { useEffect } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'ramda';
import { Trans } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useTranslate } from '../../translate/I18n';
import { commonRoutes } from '../../routes';
import { BEELANCE, ROLES } from '../../constants';
import Role from './form/Role';
import PasswordChecker from '../molecules/PasswordChecker';
import Email from '../elements/formHook/Email';
import Password from '../elements/formHook/Password';
import Firstname from '../elements/formHook/Firstname';
import Disclaimer from '../elements/formHook/Disclaimer';
import DisclaimerEmails from '../elements/formHook/DisclaimerEmails';
import DisclaimerText from '../molecules/composed/DisclaimerText';
import Lastname from '../elements/formHook/Lastname';
import PhoneNumber from '../elements/formHook/PhoneNumber';
import ConfirmPassword from '../elements/formHook/ConfirmPassword';
import { toast } from 'react-toastify';
import Box from '../atoms/Box';
import Button from '../atoms/Button';
import Link from '../atoms/Link';
import Text from '../atoms/Text';
import { emitEvent, EVENTS } from '../../utils/analytics';
import { useParams } from '../../utils/router';
import InfoCircle from '../atoms/icons/InfoCircle';
import { fallbackErrorHandler, handle, setFormHookError } from '../../infrastructure/validation/error';
import { register } from '../../store/auth/auth.service';
import { saveTokenInBrowser } from '../../store/auth/models/reauth';
import { dispatch } from '../../store/configureStore';
import { translateRoute } from '../../routesActions/routeHelper';

const { general: { login } } = commonRoutes;
const { emails } = BEELANCE;
const availableRoles = [ROLES.COMPANY, ROLES.FREELANCE];

const StyledRegister = styled('div')(({ theme }) => ({
  display: 'block',
  maxWidth: '600px',
  margin: 'auto',
  padding: `${theme.space[2]}px ${theme.space[1]}px`
}));

const Register = () => {
  const formHook = useForm();
  const params = useParams();
  const { handleSubmit, errors, watch, setError, formState, setValue } = formHook;
  const hasError = !isEmpty(errors);
  const { t, i18n } = useTranslate(['auth', 'validation', 'common', 'legal']);

  useEffect(() => {
    if (params.role && availableRoles.includes(params.role)) {
      setValue('role', params.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.role]);

  const onSubmit = async postedUser => {
    try {
      const {
        phone: { country, number },
        disclaimer,
        ...otherDetails
      } = postedUser;
      emitEvent(EVENTS.REGISTER[postedUser.role.toUpperCase()]);
      const { token, user: savedUser } = await register({
        ...otherDetails,
        disclaimer: !!disclaimer,
        language: i18n.language.toUpperCase(),
        phoneCountry: country,
        phoneNumber: number
      });
      dispatch.reauth.setToken(token);
      saveTokenInBrowser(token);
      await dispatch.reauth.loadCurrentUser(savedUser);
    } catch (e) {
      if (!e.isBackendError) {
        toast.error(e.message);
        return;
      }
      if (e.errors.length === 0) {
        toast.error(e.message);
      } else {
        for (const error of e.errors) {
          handle(e).with(setFormHookError(setError), fallbackErrorHandler(toast.error, t));
        }
      }
    }
  };

  return (
    <StyledRegister>
      <Box textAlign="center">
        <Text tag="h1" styleName="wH1b">
          {t('auth:welcome')}
        </Text>
        <Text mb={3} tag="p" styleName="wPNormal">
          {t('auth:join')}
        </Text>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Role formHook={formHook} />
        <Box
          borderRadius="8px"
          border="1px solid #e3e4e6"
          padding="12px 16px"
          mb={2}
          maxWidth="600px"
          minHeight="60px"
          backgroundColor="#f9fafb"
          display="flex">
          <InfoCircle fontSize="16px" />
          <Text ml={2}> {t('auth:needEuropeanOrSwissVat')} </Text>
        </Box>
        <Box
          display={{ _: 'flex', md: 'grid' }}
          flexDirection="column"
          gridTemplateColumns="1fr 1fr"
          rowGap={{ _: '16px', md: '48px' }}
          columnGap="32px">
          <Firstname formHook={formHook} />
          <Lastname formHook={formHook} />
        </Box>
        <Email formHook={formHook} />
        <PhoneNumber formHook={formHook} />
        <Password formHook={formHook} showError={false} />

        <Box mb={2} mt={'-20px'}>
          <PasswordChecker password={watch('password')} isSubmitted={formState.isSubmitted} />
        </Box>
        <ConfirmPassword formHook={formHook} password={watch('password')} />
        <Box mb={3}>
          <Disclaimer formHook={formHook} />
        </Box>
        <Box mb={3}>
          <DisclaimerEmails formHook={formHook} />
        </Box>

        {hasError && (
          <Text mb={3} tag="p" styleName="wPError">
            {t('common:errorsInForm')}
          </Text>
        )}
        <Button
          data-cy={'button-register'}
          margin="auto"
          display="block"
          shape="rounded"
          type="submit"
          loading={formState.isSubmitting}>
          {t('auth:register')}
        </Button>
      </form>
      <Box textAlign="center">
        <Link type="tertiary" to={login.to}>
          <Button mt={3} styleType="transparent">
            {t('auth:alreadyHaveAccount')}
          </Button>
        </Link>
      </Box>
      <DisclaimerText />
      <Box>
        <Trans i18nKey="auth:disclaimer" i18n={i18n}>
          <Text>
            __text_part_1__
            <Link type="tertiary" to={translateRoute(commonRoutes.legal.privacyPolicy.to, 'en', i18n.language)} inNewWindow>
              {t(commonRoutes.legal.privacyPolicy.label)}
            </Link>
          </Text>
          <Text>
            __text_part_2__
            <a href={`mailto:${emails.privacypolicy}`}>{emails.privacypolicy}</a>
          </Text>
        </Trans>
      </Box>
    </StyledRegister>
  );
};

export default (Register);
